import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { ScrollIcon } from '../components/icons/Icons';
import LoadingSpinner from '../components/LoadingSpinner';
import authService from '../services/authService';
import apiClient from '../services/api';
import { getRankByPoints, getRankEmoji, getTierColor, getRankImagePath } from '../services/ranksService';
// Removed calculateLevel import - using backend data for production

interface LeaderboardEntry {
  rank: number;
  userId: string;
  displayName: string;
  profilePicture?: string;
  totalPoints: number;
  questionsAnswered: number;
  correctAnswers: number;
  accuracy: number;
  bestStreak: number;
  level: number;
  gamesPlayed: number;
  isCurrentUser?: boolean;
  userRank: any;
  rankEmoji: string;
  tierColor: string;
  rankImagePath: string;
}

const LeaderboardPage: React.FC = () => {
  const { user, isAuthenticated, isLoading: authLoading, getAccessTokenSilently } = useAuth0();
  const [leaderboardData, setLeaderboardData] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [userRank, setUserRank] = useState<number | null>(null);
  
  // Ensure backend login
  useEffect(() => {
    if (isAuthenticated && user) {
      const localUser = authService.getCurrentUser();
      if (!localUser) {
        const syncUser = async () => {
          try {
            const accessToken = await getAccessTokenSilently({
              authorizationParams: {
                audience: process.env.REACT_APP_AUTH0_AUDIENCE,
                scope: 'openid profile email'
              }
            });
            await authService.loginWithAuth0(user, accessToken);
          } catch (err) {
            console.error('Failed to sync user with backend:', err);
          }
        };
        syncUser();
      }
    }
  }, [isAuthenticated, user, getAccessTokenSilently]);

  // Fetch user rank when authenticated
  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      const fetchUserRank = async () => {
        try {
          const currentUserId = authService.getCurrentUser()?.auth0Id;
          if (currentUserId) {
            const response = await apiClient.get(`/user-rank/${encodeURIComponent(currentUserId)}`);
            setUserRank(response.data.rank);
          }
        } catch (error) {
          console.error('Failed to fetch user rank:', error);
          setUserRank(null);
        }
      };

      fetchUserRank();
    } else {
      setUserRank(null);
    }
  }, [isAuthenticated, authLoading]);

  // Fetch leaderboard data
  useEffect(() => {
    const fetchLeaderboard = async () => {
      try {
        setLoading(true);
        const response = await apiClient.get('/leaderboard', {
          params: {
            limit: pageSize,
            page: currentPage
          }
        });

        // Handle different response formats
        const leaderboard = response.data.data?.leaderboard || response.data.leaderboard || response.data || [];
        const stats = response.data.data?.stats || response.data.stats || {};

        // Mark current user if authenticated
        const currentUserId = authService.getCurrentUser()?.auth0Id;

        // Get pagination info from response
        const totalUsers = stats.totalPlayers || leaderboard.length;
        const totalPages = stats.totalPages || Math.ceil(totalUsers / pageSize);
        setTotalUsers(totalUsers);
        setTotalPages(totalPages);

        const processedData = leaderboard.map((entry: any, index: number) => {
            const userPoints = entry.totalPoints || 0;
            const userRank = getRankByPoints(userPoints);
            const rankEmoji = getRankEmoji(userRank.name);
            const tierColor = getTierColor(userRank.tier);
            const rankImagePath = getRankImagePath(userRank.name);

            return {
              rank: (currentPage - 1) * pageSize + index + 1, // Calculate actual rank based on page
              userId: entry._id,
              displayName: entry.displayName || entry.name,
              profilePicture: entry.picture,
              totalPoints: userPoints,
              questionsAnswered: entry.totalAnswers || 0,
              correctAnswers: entry.correctAnswers || 0,
              accuracy: entry.totalAnswers > 0
                ? Math.round((entry.correctAnswers / entry.totalAnswers) * 100)
                : 0,
              bestStreak: entry.bestStreak || 0,
              level: entry.currentLevel || 1,
              gamesPlayed: entry.gamesPlayed || 0,
              isCurrentUser: currentUserId === entry.auth0Id,
              userRank: userRank,
              rankEmoji: rankEmoji,
              tierColor: tierColor,
              rankImagePath: rankImagePath
            };
          });

          setLeaderboardData(processedData);
      } catch (err) {
        console.error('Failed to fetch leaderboard:', err);
        setError('Failed to load leaderboard. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchLeaderboard();
  }, [currentPage, pageSize]);

  // Helper function to get medal for top 3 ranks
  const getMedalEmoji = (rank: number) => {
    switch (rank) {
      case 1: return '🥇';
      case 2: return '🥈';
      case 3: return '🥉';
      default: return '';
    }
  };

  // Pagination functions
  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page when changing page size
  };


  if (loading || authLoading) {
    return (
      <div className="leaderboard-container fade-in">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="leaderboard-container fade-in">
        <div className="error-message">
          <p>{error}</p>
          <button onClick={() => window.location.reload()} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="leaderboard-container fade-in">
      {/* Bible Verse Watermark */}
      <div className="bible-verse-watermark">
        <p>"For the Lord gives wisdom; from his mouth come knowledge and understanding."</p>
        <span className="bible-reference">- Proverbs 2:6</span>
      </div>

      {/* Decorative elements */}
      <div className="leaderboard-decoration leaderboard-decoration-1"></div>
      <div className="leaderboard-decoration leaderboard-decoration-2"></div>
      <div className="leaderboard-decoration leaderboard-decoration-3"></div>
      
      {/* Additional decorative stars */}
      <div className="star star-1" style={{ top: '15%', right: '15%', width: '30px', height: '30px', opacity: '0.2', animation: 'bounce 3s infinite ease-in-out' }}></div>
      <div className="star star-2" style={{ top: '35%', left: '10%', width: '20px', height: '20px', opacity: '0.15', animation: 'bounce 4s infinite ease-in-out' }}></div>
      <div className="star star-3" style={{ bottom: '25%', right: '8%', width: '25px', height: '25px', opacity: '0.2', animation: 'bounce 5s infinite ease-in-out' }}></div>
      
      <div className="leaderboard-content">
        <header className="leaderboard-header">
          <h1 className="leaderboard-title">
            <span className="leaderboard-crown">👑</span>
            Leaderboard
          </h1>
          <div className="leaderboard-subtitle-container">
            <p className="leaderboard-subtitle">
              Discover our highest-ranking Bible scholars and see where you stand!
            </p>
            {isAuthenticated && (
              <div className="user-rank-info">
                <span className="user-rank-label">Your Rank:</span>
                <span className="user-rank-value">
                  {userRank !== null ? `#${userRank}` : 'Loading...'}
                </span>
              </div>
            )}
          </div>
        </header>
        
        
        {/* Desktop Table View */}
        <div className="leaderboard-table-container">
          <div className="leaderboard-table-header">
            <div className="leaderboard-column leaderboard-rank">Rank</div>
            <div className="leaderboard-column leaderboard-name">Player</div>
            <div className="leaderboard-column leaderboard-score">Score</div>
            <div className="leaderboard-column leaderboard-level">Biblical Rank</div>
          </div>
          
          <div className="leaderboard-table-body">
            {leaderboardData.length === 0 ? (
              <div className="no-data-message">
                <p>No leaderboard data available yet. Be the first to complete a game!</p>
                <Link to="/play" className="play-now-button">
                  Play Now
                </Link>
              </div>
            ) : (
              leaderboardData.map((entry) => (
                <div 
                  key={entry.userId} 
                  className={`leaderboard-row ${entry.rank <= 3 ? 'leaderboard-top-rank leaderboard-top-' + entry.rank : ''} ${entry.isCurrentUser ? 'current-user-row' : ''}`}
                >
                  <div className="leaderboard-column leaderboard-rank">
                    {getMedalEmoji(entry.rank) || <span className="rank-number" style={{ fontFamily: 'var(--number-font)' }}>{entry.rank}</span>}
                  </div>
                  <div className="leaderboard-column leaderboard-name">
                    <div className="player-info">
                      <img 
                        src={entry.rankImagePath} 
                        alt={entry.userRank.name}
                        className="player-avatar player-rank-avatar"
                        title={entry.userRank.name}
                      />
                      <span className="player-name">
                        {entry.displayName}
                        {entry.isCurrentUser && <span className="you-badge">(You)</span>}
                      </span>
                    </div>
                  </div>
                  <div className="leaderboard-column leaderboard-score" style={{ fontFamily: 'var(--number-font)' }}>{entry.totalPoints.toLocaleString()}</div>
                  <div className="leaderboard-column leaderboard-level">
                    <span className="rank-badge" style={{ color: entry.tierColor }}>
                      {entry.userRank.name}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Mobile Card View */}
        <div className="leaderboard-mobile-container">
          {leaderboardData.length === 0 ? (
            <div className="no-data-message">
              <p>No leaderboard data available yet. Be the first to complete a game!</p>
              <Link to="/play" className="play-now-button">
                Play Now
              </Link>
            </div>
          ) : (
            leaderboardData.map((entry) => (
              <div 
                key={entry.userId} 
                className={`leaderboard-mobile-card ${entry.rank <= 3 ? `top-rank-${entry.rank}` : ''} ${entry.isCurrentUser ? 'current-user' : ''}`}
              >
                <div className="leaderboard-mobile-header">
                  <div className="leaderboard-mobile-rank">
                    {getMedalEmoji(entry.rank) ? (
                      <span className="leaderboard-mobile-rank-emoji">{getMedalEmoji(entry.rank)}</span>
                    ) : (
                      <div className="leaderboard-mobile-rank-number">{entry.rank}</div>
                    )}
                    <div className="leaderboard-mobile-player">
                      <div className="leaderboard-mobile-avatar">
                        <img 
                          src={entry.rankImagePath} 
                          alt={entry.userRank.name}
                          className="mobile-rank-avatar"
                          title={entry.userRank.name}
                        />
                      </div>
                      <div>
                        <div className="leaderboard-mobile-name">
                          <span className="leaderboard-mobile-name-text">{entry.displayName}</span>
                          {entry.isCurrentUser && <span className="leaderboard-mobile-you-badge">YOU</span>}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="leaderboard-mobile-stats">
                  <div className="leaderboard-mobile-stat">
                    <div className="leaderboard-mobile-stat-value" style={{ color: entry.tierColor }}>
                      {entry.userRank.name}
                    </div>
                    <div className="leaderboard-mobile-stat-label">Biblical Rank</div>
                  </div>
                  <div className="leaderboard-mobile-stat">
                    <div className="leaderboard-mobile-stat-value">{entry.totalPoints.toLocaleString()}</div>
                    <div className="leaderboard-mobile-stat-label">Points</div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="leaderboard-pagination">
            <div className="pagination-controls">
              <button
                className="pagination-btn pagination-btn-secondary"
                onClick={goToPreviousPage}
                disabled={currentPage === 1}
              >
                Previous
              </button>

              <div className="pagination-pages">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const pageNumber = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                  if (pageNumber > totalPages) return null;

                  return (
                    <button
                      key={pageNumber}
                      className={`pagination-btn ${pageNumber === currentPage ? 'pagination-btn-primary' : 'pagination-btn-secondary'}`}
                      onClick={() => goToPage(pageNumber)}
                    >
                      {pageNumber}
                    </button>
                  );
                })}
              </div>

              <button
                className="pagination-btn pagination-btn-secondary"
                onClick={goToNextPage}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>

            <div className="pagination-info">
              <select
                value={pageSize}
                onChange={(e) => handlePageSizeChange(parseInt(e.target.value))}
                className="page-size-select"
              >
                <option value={10}>10 per page</option>
                <option value={25}>25 per page</option>
                <option value={50}>50 per page</option>
                <option value={100}>100 per page</option>
              </select>
            </div>
          </div>
        )}

        <div className="leaderboard-info-box">
          <div className="leaderboard-info-header">
            <ScrollIcon size={24} color="#fcd34d" />
            <h3>How Scoring Works</h3>
          </div>
          <p>Scores are calculated based on correct answers, response time, streak bonuses, and question difficulty. Keep playing daily to build your streak and climb the rankings!</p>
        </div>
        
        <div className="leaderboard-actions">
          <a href="/" className="leaderboard-btn leaderboard-btn-primary">Start a New Game</a>
          <a href="/about" className="leaderboard-btn leaderboard-btn-secondary">About KNOW</a>
        </div>
        
        <footer className="leaderboard-footer">
          <p>Leaderboard updates in real-time. Last refreshed: {new Date().toLocaleString()}</p>
        </footer>
      </div>
    </div>
  );
};

export default LeaderboardPage;